import { CSSProperties, useEffect, useState } from "react";
import Select from "react-select";
import { useLocation, useNavigate } from "react-router-dom";

import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {Dropdown, DropdownButton } from "react-bootstrap";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import classNames from "classnames";
import { LANGUAGES } from "../../constants";
import {
    changeDemoModeState,
    changeMobileDrawerMenuShowState,
    toggleSwapToOrderAddedItemsInMobileView,
    toggleShowSettingsModal,
    setShowSendMenuLinkModal
} from "../../store/slices/appGeneralSlice";
import { InfoCircleItalicIcon, PlusSquareIcon } from "../../components/svgIcons/AllIcons";
import logoIcon from "./../../components/images/app-icon-circle.png";
import { useRestaurantDetails } from "../providers/RestaurantDetailsContext";
import { CircleSpinner } from "../../components/Loaders";
import {toggleOpenPaymentsModal } from "../../store/slices/paymentManagementSlice";
import OrdersNotification from "../../components/OrdersNotification";
import { useOrderFlow } from "../providers/OrderFlowProvider";
import { useAuth } from "../providers/AuthContext";
import SendMenuLinkModal from "../../components/Layouts/SendMenuLinkModal";

import "./Header.scss";

const HIDING_DEMO_MODE_BANNER_TRANSITION_DURATION_MS = 350;

const dialogModalInst = withReactContent(Swal);

const Header = () => {
    const {i18n, t} = useTranslation();

    const dispatch = useDispatch();

    const navigate = useNavigate();

    const location = useLocation();
    const currentPath = location.pathname;

    const isOnDashboardPage = currentPath === "/";
    const isOnPaymentPage = currentPath === "/payment";

    const {
        logout
    } = useAuth();

    const {
        goToTablesListStepAndUpdateData,
        updateOrdersList,
        selectOrder,
        unselectOrder,
        toggleDemoMode,
    } = useOrderFlow();

    const {
        restaurantDetails,
        setRestaurantDetails,
        clearRestaurantDetails,
    } = useRestaurantDetails();

    const {
        // restaurant_name = "",
        restaurant_logo,
        waiter_name,
        waiter_email,
        is_demo_mode: isDemoMode_initVal,
        waiter_permissions,
    } = restaurantDetails || {};

    const canUseDemoMode = waiter_permissions?.demo_mode !== false;
    const canViewOrdersReport = waiter_permissions?.orders_report !== false;

    const isDemoMode = useSelector((state: RootState) => state.appGeneral.isDemoMode);
    const isMobileView = useSelector((state: RootState) => state.appGeneral.isMobileView);
    const currentTableId = useSelector((state: RootState) => state.stepManagement.currentTableId);
    const currentOrderType = useSelector((state: RootState) => state.stepManagement.currentOrderType);
    const currentOrderId = useSelector((state: RootState) => state.stepManagement.currentOrderId);
    const showOrderAddedItemsBoxInMobileView = useSelector((state: RootState) => state.appGeneral.showOrderAddedItemsBoxInMobileView);
    const showDemoModeBtnLoader = useSelector((state: RootState) => state.appGeneral.showDemoModeBtnLoader);

    const [demoModeBannerVisibilityState, setDemoModeBannerVisibilityState] = useState<"visible" | "hidden">();

    const initialLanguageCode = localStorage.getItem("langCode") || "en";

    useEffect(() => {
        i18n.changeLanguage(initialLanguageCode);
    }, []);

    useEffect(() => {
        if (isDemoMode) {
            if (demoModeBannerVisibilityState === undefined) {
                setDemoModeBannerVisibilityState("visible");
            } else {
                setTimeout(() => {
                    if (demoModeBannerVisibilityState !== "visible") {
                        setDemoModeBannerVisibilityState("visible");
                    }
                }, 0);
            }
        } else if (isDemoMode === false && demoModeBannerVisibilityState === "visible") {
            setTimeout(() => {
                setDemoModeBannerVisibilityState("hidden");
            }, HIDING_DEMO_MODE_BANNER_TRANSITION_DURATION_MS);
        }
    }, [isDemoMode, demoModeBannerVisibilityState]);

    useEffect(() => {
        dispatch(changeDemoModeState({
            isDemoMode: canUseDemoMode && !!isDemoMode_initVal,
        }));
    }, [canUseDemoMode, dispatch, isDemoMode_initVal]);


    const handleUserLogout = (e) => {
        e.preventDefault();
        logout();
        clearRestaurantDetails();
    };

    const onChangeLang = (newOpt) => {
        const lang_code = newOpt?.value;
        localStorage.setItem("langCode", lang_code);
        i18n.changeLanguage(lang_code);

        // window.location.reload(false);
        window.location.reload();
    };

    const HandleGoBackToTheDahboardPage = () => {
        // navigate(-1);

        const currSP_str = new URLSearchParams(window.location.search).toString();
        navigate(`/${currSP_str ? "?" + currSP_str : ""}`);

        if (currentOrderId) {
            selectOrder({
                orderId: currentOrderId,
            });
        }
        else if (currentTableId || currentOrderType) {
            updateOrdersList({
                tableId: currentTableId,
                orderType: currentOrderType,
            });
        }
        else {
            goToTablesListStepAndUpdateData();
        }
    };

    const handleBackToTablesList = () => {
        navigate("/");

        goToTablesListStepAndUpdateData();
    };

    const handleBackToOrdersPreviewBox = () => {
        dispatch(toggleSwapToOrderAddedItemsInMobileView({
            swapToOrderAddedItemsBox: false
        }));
        unselectOrder();
    };

    const handleToggleDemoMode = async () => {
        const isSwitchingToLiveMode = isDemoMode;

        dialogModalInst.fire({
            icon: "warning",
            ...(isSwitchingToLiveMode ? {
                title: String(t("areYouSureYouWantToSwitchToLiveMode")),
            } : {
                title: String(t("areYouSureYouWantToSwitchToDemoMode")),
                html: String(t("demoModeAlertMessage")),
            }),
            showDenyButton: false,
            showCancelButton: true,
            confirmButtonText: String(t("yes")),
            cancelButtonText: String(t("no")),
            focusCancel: true,
            allowOutsideClick: true,
            showCloseButton: true,
            customClass: {
                popup: 'custom-swal-alert-dialog',
                confirmButton: 'action-btn confirm-btn mx-2',
                cancelButton: 'action-btn cancel-btn mx-2',
            },
        }).then((confirmResult) => {
            if (!confirmResult.isConfirmed) return;

            toggleDemoMode({
                demoMode: !isDemoMode,
                afterSuccess: (is_demo_mode) => {
                    setRestaurantDetails({
                        ...(restaurantDetails || {}),
                        is_demo_mode,
                    });
                }
            });
        });
    };

    const handleShowOpenPeysModal = async () => {
        dispatch(toggleOpenPaymentsModal({
            showModal: true,
        }));
    };

    const handleShowMobileDrawerMenu = () => {
        dispatch(changeMobileDrawerMenuShowState({
            show: true
        }));
    };

    const handleOpenSettingsModal = () => {
        dispatch(toggleShowSettingsModal({
            showModal: true
        }));
    };

    const handleOpenSendMenuLinkModal = () => {
        dispatch(setShowSendMenuLinkModal(true))
    };

    const handleShowDemoModeAlertInfo = () => {
        dialogModalInst.fire({
            // title: String(t("demoModeAlertMessage")),
            icon: "info",
            html: String(t("demoModeAlertMessage")),
            // showCloseButton: true,
            focusConfirm: true,
            confirmButtonText: String(t('iUnderstand')),
            allowOutsideClick: false,
            customClass: {
                popup: 'custom-swal-alert-dialog'
            },
        });
    };

    return (
        <>
            <header className="header-navbar">
                {(isDemoMode || demoModeBannerVisibilityState === "visible") && (
                    <div
                        className={classNames("demo-mode-banner d-flex justify-content-center align-items-center", {
                            "is-visible": isDemoMode && demoModeBannerVisibilityState === "visible",
                        })}
                        style={{
                            "--hiding-demo-mode-banner-duration": `${HIDING_DEMO_MODE_BANNER_TRANSITION_DURATION_MS}ms`
                        } as CSSProperties}
                    >
                        <span
                            className="banner-text d-inline-flex align-items-center"
                            onClick={handleShowDemoModeAlertInfo}
                        >
                            <span className="icon me-2">
                                {/* <CircleInfoIcon /> */}
                                <InfoCircleItalicIcon />
                            </span>
                            {t("demoModeIsActive")}
                        </span>
                    </div>
                )}
                <div className="navbar navbar-inner">
                    <div className="navbar-menu d-flex flex-grow-1">
                        {!isOnDashboardPage ? (
                            <div className="d-flex align-items-center me-3">
                                <button
                                    className="btn go-back-icon-btn icon-btn btn-outline-secondary rounded-circle"
                                    onClick={HandleGoBackToTheDahboardPage}
                                >
                                    <span className="icon">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="currentColor" fillRule="evenodd"><path d="M0 8a1 1 0 0 1 1-1h14a1 1 0 1 1 0 2H1a1 1 0 0 1-1-1z"/><path d="M8.707.293a1 1 0 0 1 0 1.414L2.414 8l6.293 6.293a1 1 0 0 1-1.414 1.414l-7-7a1 1 0 0 1 0-1.414l7-7a1 1 0 0 1 1.414 0z"/></svg>
                                    </span>
                                </button>
                            </div>
                        ) : (isMobileView && showOrderAddedItemsBoxInMobileView) && (
                            <button
                                className="btn go-back-icon-btn btn-outline-secondary w-auto me-2 d-flex align-items-center px-3 py-0 me-3"
                                onClick={handleBackToOrdersPreviewBox}
                            >
                                <span className="icon btn-icon">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="currentColor" fillRule="evenodd"><path d="M0 8a1 1 0 0 1 1-1h14a1 1 0 1 1 0 2H1a1 1 0 0 1-1-1z"/><path d="M8.707.293a1 1 0 0 1 0 1.414L2.414 8l6.293 6.293a1 1 0 0 1-1.414 1.414l-7-7a1 1 0 0 1 0-1.414l7-7a1 1 0 0 1 1.414 0z"/></svg>
                                </span>
                                <span className="ms-2">
                                    {t("ordersList")}
                                </span>
                            </button>
                        )}

                        {isOnPaymentPage && (
                            <span className="me-2">
                                <button
                                    className="back-to-table-list-btn btn btn-outline-secondary fw-medium px-3"
                                    onClick={handleBackToTablesList}
                                >
                                    <span className="icon btn-icon me-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="currentColor" fillRule="evenodd"><path d="M0 8a1 1 0 0 1 1-1h14a1 1 0 1 1 0 2H1a1 1 0 0 1-1-1z"/><path d="M8.707.293a1 1 0 0 1 0 1.414L2.414 8l6.293 6.293a1 1 0 0 1-1.414 1.414l-7-7a1 1 0 0 1 0-1.414l7-7a1 1 0 0 1 1.414 0z"/></svg>
                                    </span>
                                    <span className="btn-text">
                                        {t("tablesList")}
                                    </span>
                                </button>
                            </span>
                        )}

                        {isOnDashboardPage && canUseDemoMode && (
                            <span className="me-2">
                                <button
                                    className={classNames("dm-switch-btn p-0 border-0", {
                                        "active": isDemoMode,
                                        "show-loader": showDemoModeBtnLoader,
                                    })}
                                    onClick={handleToggleDemoMode}
                                    disabled={showDemoModeBtnLoader}
                                >
                                    {showDemoModeBtnLoader && (
                                        <span className="icon loader-icon">
                                            <CircleSpinner
                                                circleOpacity={0}
                                                spinnerColor="#fff"
                                                thickness={5}
                                            />
                                        </span>
                                    )}
                                    <span className="btn-text">
                                        D
                                    </span>
                                </button>
                            </span>
                        )}

                        <span>
                            <button
                                className="pwa-install-prompt-btn btn btn-flat"
                                title={t("addDAMToHomeScreen") || undefined}
                                style={{
                                    display: "none"
                                }}
                            >
                                <span className="icon">
                                    <PlusSquareIcon/>
                                </span>
                                <span className="btn-text-wrapper">
                                    <span className="btn-text">
                                        {t("installDAM")}
                                    </span>
                                </span>
                            </button>
                        </span>

                        <div className="flex-grow-1 text-md-right clearfix d-flex justify-content-end">
                            {!isMobileView ? (
                                <>
                                    <OrdersNotification />

                                    {canViewOrdersReport && (
                                        <button
                                            className="btn btn-outline-success fw-medium me-3 text-capitalize px-1 px-sm-3"
                                            onClick={handleShowOpenPeysModal}
                                        >
                                            {t("openPayments")}
                                        </button>
                                    )}

                                    <div className="navbar-language-select select2-elem-wrapper me-2 me-sm-4">
                                        <Select
                                            id="change_language_select"
                                            // title="language"
                                            // name="language"
                                            classNamePrefix="select"
                                            // className={"basic-single custom-select__menu-scrollbar"}
                                            // className="form-control"
                                            // styles={selectStyles}
                                            options={LANGUAGES}
                                            defaultValue={LANGUAGES[0]}
                                            onChange={onChangeLang}
                                            value={LANGUAGES.find(lang => lang.value === initialLanguageCode) || ""}
                                            // formatOptionLabel={handleFormatOptionLabel}
                                            // menuPortalTarget={document.body}
                                            // isDisabled={false}
                                            // isLoading={false}
                                            isClearable={false}
                                            isSearchable={true}
                                            menuPlacement="bottom"
                                        />
                                    </div>

                                    <DropdownButton
                                        // id="userNavbarDropdownMenu"
                                        as="div"
                                        align="end"
                                        className="user-navbar-dropdown"
                                        title={
                                            <>
                                                <img
                                                    src={logoIcon}
                                                    className="header-profile-img"
                                                    width="40"
                                                    height="40"
                                                    alt={String(t("appLogoAlt"))}
                                                />
                                                <span className="user-name">
                                                    <span className="user-name-text text-capitalize">
                                                        {/* {t("logout")} */}
                                                        {/* {restaurant_name} */}
                                                        {waiter_name}
                                                    </span>
                                                </span>
                                            </>
                                        }
                                    >
                                        <Dropdown.ItemText
                                            className="d-flex align-items-center"
                                        >
                                            <span className="avatar-img">
                                                <img
                                                    src={restaurant_logo || logoIcon}
                                                    width="40"
                                                    height="40"
                                                    alt={String(t("appLogoAlt"))}
                                                />
                                            </span>
                                            <div className="user-general-info d-inline-block">
                                                <div className="user-name text-capitalize">
                                                    {/* {restaurant_name} */}
                                                    {waiter_name}
                                                </div>
                                                <div className="user-id">
                                                    {waiter_email}
                                                </div>
                                            </div>
                                        </Dropdown.ItemText>
                                        <Dropdown.Divider/>
                                        {/* <Dropdown.Item
                                        className="text-capitalize"
                                    >
                                        <span className="icon">
                                            <svg stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>
                                        </span>
                                        <span>
                                            {t("profile")}
                                        </span>
                                    </Dropdown.Item> */}
                                        <Dropdown.Item
                                            className="text-capitalize"
                                            onClick={handleOpenSettingsModal}
                                        >
                                            <span className="icon">
                                                <svg stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                                            </span>
                                            <span>
                                                {t("settings")}
                                            </span>
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                            className="text-capitalize"
                                            onClick={handleOpenSendMenuLinkModal}
                                        >
                                            <span className="icon">
                                                <svg stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.99 9.99 0 01-4.255-.949L3 20l1.162-3.874A7.948 7.948 0 013 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/></svg>
                                            </span>
                                            <span>
                                                {t("sendMenuSms")}
                                            </span>
                                        </Dropdown.Item>
                                        <Dropdown.Divider/>
                                        <Dropdown.Item
                                            className="text-capitalize"
                                            onClick={handleUserLogout}
                                        >
                                            <span className="icon">
                                                <svg stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path></svg>
                                            </span>
                                            <span>
                                                {/* {t("logout")} */}
                                                {t("signOut")}
                                            </span>
                                        </Dropdown.Item>
                                    </DropdownButton>
                                </>
                            ) : (
                                <>
                                    <button
                                        className="btn mobile-hamburger-btn px-3"
                                        onClick={handleShowMobileDrawerMenu}
                                    >
                                        <span className="icon">
                                            <svg height="22pt" width="22pt" viewBox="0 -53 384 384" xmlns="http://www.w3.org/2000/svg"><path d="m368 154.667969h-352c-8.832031 0-16-7.167969-16-16s7.167969-16 16-16h352c8.832031 0 16 7.167969 16 16s-7.167969 16-16 16zm0 0"/><path d="m368 32h-352c-8.832031 0-16-7.167969-16-16s7.167969-16 16-16h352c8.832031 0 16 7.167969 16 16s-7.167969 16-16 16zm0 0"/><path d="m368 277.332031h-352c-8.832031 0-16-7.167969-16-16s7.167969-16 16-16h352c8.832031 0 16 7.167969 16 16s-7.167969 16-16 16zm0 0"/></svg>
                                        </span>
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            <SendMenuLinkModal />
        </>
    )
}

export default Header;
